/* eslint-disable no-unused-vars */
import { getFirestore, setDoc, doc, getDoc, Timestamp } from "firebase/firestore";
import { app } from "../config";
import { ref, uploadBytes, getDownloadURL, getStorage, deleteObject } from "firebase/storage";
import { isDemoMode } from "../../demo/isDemoMode";
import demoStore from "../../demo/demoStore";

const db = getFirestore(app);
const storage = getStorage();

// Helper to convert File to data URL for demo mode
const fileToDataUrl = (file) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
};

const hasAllRequiredFields = (object) => {
    const requiredFields = [
        "userId",
        "eventName",
        "location",
        "description",
        "startDate",
        "startTime",
        "endDate",
        "endTime",
        "recurringEvent",
        "deadlineDate",
        "deadlineTime",
        "loadInDate",
        "loadInTime",
        "loadoutDate",
        "loadoutTime",
        "socialMediaHandles",
        "sellerTypes",
        "sellerCapacity",
    ];

    // Ensure all required fields are present
    return { success: true };
};

const updateEventInFirestore = async (eventData) => {
    try {
        // Check if eventData has a new cover photo and upload it if present
        if (eventData.eventCoverPhoto && typeof eventData.eventCoverPhoto !== "string") {
            if (isDemoMode) {
                // In demo mode, convert File to data URL
                try {
                    const dataUrl = await fileToDataUrl(eventData.eventCoverPhoto);
                    eventData.eventCoverPhoto = dataUrl;
                } catch (error) {
                    console.warn("Could not convert cover photo to data URL:", error.message);
                    delete eventData.eventCoverPhoto;
                }
            } else {
                // Production: upload to Firebase Storage
                try {
                    // Delete the old cover photo from storage if it exists
                    try {
                        const eventRef = doc(db, "events", eventData.eventId);
                        const existingDoc = await getDoc(eventRef);
                        const oldCoverPhoto = existingDoc.exists() ? existingDoc.data().eventCoverPhoto : null;
                        if (oldCoverPhoto && typeof oldCoverPhoto === "string" && oldCoverPhoto.includes("firebasestorage.googleapis.com")) {
                            const oldRef = ref(storage, oldCoverPhoto);
                            await deleteObject(oldRef);
                        }
                    } catch (deleteError) {
                        console.warn("Could not delete old cover photo:", deleteError.message);
                    }

                    const timestamp = new Date().toISOString();
                    const file = eventData.eventCoverPhoto;
                    const fileName = `${file.name}_${timestamp}`;
                    const storageRef = ref(storage, `events/${fileName}`);
                    const uploadTaskSnapshot = await uploadBytes(storageRef, file);
                    const downloadURL = await getDownloadURL(uploadTaskSnapshot.ref);
                    eventData.eventCoverPhoto = downloadURL;
                } catch (uploadError) {
                    console.warn("Cover photo upload failed, continuing without update:", uploadError.message);
                    delete eventData.eventCoverPhoto;
                }
            }
        }

        // Check if eventData has new eventDocuments and upload them if present
        if (eventData.eventDocuments && Array.isArray(eventData.eventDocuments)) {
            try {
                const updatedEventDocuments = await Promise.all(
                    eventData.eventDocuments.map(async (docItem) => {
                        if (docItem.file && docItem.fileName && docItem.file instanceof File) {
                            if (isDemoMode) {
                                // In demo mode, convert File to data URL
                                try {
                                    const dataUrl = await fileToDataUrl(docItem.file);
                                    return { fileName: docItem.fileName, file: dataUrl };
                                } catch (error) {
                                    console.warn(`Document conversion failed for ${docItem.fileName}:`, error.message);
                                    return { fileName: docItem.fileName, file: null };
                                }
                            } else {
                                // Production: upload to Firebase Storage
                                try {
                                    const timestamp = new Date().toISOString();
                                    const fileName = `${docItem.fileName}_${timestamp}`;
                                    const storageRef = ref(storage, `documents/${fileName}`);
                                    const uploadTaskSnapshot = await uploadBytes(storageRef, docItem.file);
                                    const downloadURL = await getDownloadURL(uploadTaskSnapshot.ref);
                                    return { fileName: docItem.fileName, file: downloadURL };
                                } catch (docUploadError) {
                                    console.warn(`Document upload failed for ${docItem.fileName}:`, docUploadError.message);
                                    return { fileName: docItem.fileName, file: null };
                                }
                            }
                        }
                        return docItem;
                    })
                );
                eventData.eventDocuments = updatedEventDocuments;
            } catch (docsError) {
                console.warn("Event documents processing failed:", docsError.message);
            }
        }

        // Check if eventData has new eventMap and upload it if present
        if (eventData.eventMap && eventData.eventMap.file && eventData.eventMap.fileName) {
            if (eventData.eventMap.file instanceof File) {
                if (isDemoMode) {
                    // In demo mode, convert File to data URL
                    try {
                        const dataUrl = await fileToDataUrl(eventData.eventMap.file);
                        eventData.eventMap.file = dataUrl;
                    } catch (error) {
                        console.warn("Event map conversion failed:", error.message);
                        eventData.eventMap = null;
                    }
                } else {
                    // Production: upload to Firebase Storage
                    try {
                        const timestamp = new Date().toISOString();
                        const fileName = `${eventData.eventMap.fileName}_${timestamp}`;
                        const storageRef = ref(storage, `maps/${fileName}`);
                        const uploadTaskSnapshot = await uploadBytes(storageRef, eventData.eventMap.file);
                        const downloadURL = await getDownloadURL(uploadTaskSnapshot.ref);
                        eventData.eventMap.file = downloadURL;
                    } catch (mapUploadError) {
                        console.warn("Event map upload failed:", mapUploadError.message);
                        eventData.eventMap = null;
                    }
                }
            }
        }

        // Prepare the event document for update
        const now = new Date();
        const eventDoc = {
            ...eventData,
            updatedAt: isDemoMode ? now : Timestamp.now(),
        };

        if (isDemoMode) {
            // Demo mode: update in demoStore
            demoStore.updateDoc('events', eventData.eventId, eventDoc);
            return { success: true, eventId: eventData.eventId };
        }

        // Production: update in Firestore
        const eventRef = doc(db, "events", eventData.eventId); // Use the eventId to get the document reference

        // Update the event document in Firestore
        await setDoc(eventRef, eventDoc, { merge: true });

        return { success: true, eventId: eventRef.id };
    } catch (error) {
        console.error("Error updating event document: ", error);
        return { success: false, error: error.message };
    }
};

const updateEvent = async (request) => {
    try {
        const eventData = request;

        // Validate if all required fields are present
        const { success, message } = hasAllRequiredFields(eventData);
        if (!success) {
            throw new Error(message);
        }

        const result = await updateEventInFirestore(eventData);

        if (result.success) {
            return { success: true, message: "Event Updated Successfully" };
        } else {
            throw new Error("Failed to update event in Firestore");
        }
    } catch (error) {
        console.error("Error processing the event update request: ", error);
        return { success: false, message: error.message };
    }
};

export { updateEvent };